/**
 * organizationsSlice.ts — Redux slice for the Organizations resource.
 *
 * WEB-281.4 P2 tenancy FE. State shape: { orgs, activeOrgId, isLoading, error }.
 * Mirrors the funnelsSlice pattern.
 */
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { organizationsApi, authApi } from '@/api';
import type { CreateOrganizationPayload } from '@/api/organizations';
import type { Organization, OrganizationsState } from '@/types';
import { extractApiError, type ApiErrorInfo } from '@/api/errorStatus';
import { rejectedApiError } from './rejectedApiError';

const initialState: OrganizationsState = {
  orgs: [],
  activeOrgId: null,
  isLoading: false,
  error: null,
  autoCorrectedOrgId: null,
};

export const fetchOrganizationsThunk = createAsyncThunk(
  'organizations/fetchAll',
  async (_, { rejectWithValue }) => {
    try {
      const response = await organizationsApi.list();
      return response.data as { organizations: Organization[] };
    } catch (error: unknown) {
      return rejectWithValue(
        extractApiError(error),
      );
    }
  },
);

// WEB-329 — persist active org on server then update local state.
// On API error (e.g. 403 not-a-member) the store is NOT updated.
export const switchActiveOrgThunk = createAsyncThunk<
  string,
  string,
  { rejectValue: ApiErrorInfo }
>(
  'organizations/switchActive',
  async (orgId, { rejectWithValue }) => {
    try {
      await authApi.setActiveOrg(orgId);
      return orgId;
    } catch (error: unknown) {
      return rejectWithValue(
        extractApiError(error),
      );
    }
  },
);

export const createOrganizationThunk = createAsyncThunk(
  'organizations/create',
  async (data: CreateOrganizationPayload, { rejectWithValue }) => {
    try {
      const response = await organizationsApi.create(data);
      return response.data as Organization;
    } catch (error: unknown) {
      return rejectWithValue(
        extractApiError(error),
      );
    }
  },
);

const organizationsSlice = createSlice({
  name: 'organizations',
  initialState,
  reducers: {
    setActiveOrg(state, action: { payload: string }) {
      state.activeOrgId = action.payload;
      // Явная установка (профиль на входе, мастер, переключатель) — не
      // автопоправка: метку снимаем, чтобы реакция WEB-1928 не сработала
      // на чужой повод.
      state.autoCorrectedOrgId = null;
    },
    /**
     * WEB-1928 (в): реакция кабинета доиграла автопоправку — метку снять.
     * Снимать ОБЯЗАТЕЛЬНО до отправки запросов: пока метка стоит, экран не
     * рисует «У вас пока нет проектов» (шаг 9 сценария приёмки).
     */
    clearAutoCorrectedOrg(state) {
      state.autoCorrectedOrgId = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchOrganizationsThunk.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchOrganizationsThunk.fulfilled, (state, action) => {
        state.isLoading = false;
        state.orgs = action.payload.organizations;
        // Auto-select first org only when no selection exists yet.
        // WEB-826: и когда выбранной орги в списке БОЛЬШЕ НЕТ — её удалили или
        // пользователя из неё исключили. Раньше устаревший id оставался в
        // сторе, и следующий write уходил на него: сервер честно отвечал 403
        // «Вы не участник», хотя на экране показана совсем другая организация.
        // Держать призрак нельзя — если орг не осталось, активной тоже нет.
        const stillMember =
          state.activeOrgId !== null &&
          action.payload.organizations.some((o) => o.id === state.activeOrgId);
        if (!stillMember) {
          const replacement = action.payload.organizations[0]?.id ?? null;
          state.activeOrgId = replacement;
          // WEB-1928 (в): поправка сделана ТОЛЬКО в браузере — сама по себе
          // она не перезапрашивает проекты (они уже уехали с негодным
          // идентификатором и вернулись пустыми) и не доезжает до сервера,
          // поэтому следующий вход повторяет тот же пустой кабинет. Метка —
          // единственный признак «поправка ещё не доиграна»; её читает
          // реакция hooks/useActiveOrgSync.ts. Подставленный null писать
          // некуда (организаций нет вовсе) — метку не ставим.
          if (replacement !== null) {
            state.autoCorrectedOrgId = replacement;
          }
        }
        state.error = null;
      })
      .addCase(fetchOrganizationsThunk.rejected, (state, action) => {
        state.isLoading = false;
        state.error = rejectedApiError(action);
      })
      .addCase(createOrganizationThunk.fulfilled, (state, action) => {
        state.orgs = [...state.orgs, action.payload];
        state.activeOrgId = action.payload.id;
        state.error = null;
      })
      .addCase(createOrganizationThunk.rejected, (state, action) => {
        state.error = rejectedApiError(action);
      })
      // Ревизия пачки 24, находка 1 (WEB-1928, конец (в)). Метку автопоправки
      // снимает и ЭТОТ путь — единственная дверь, которая пишет активную
      // организацию на сервер (`authApi.setActiveOrg`). Как только её позвали,
      // у поправки появился хозяин: доигрывать её реакцией больше нечего, и
      // второй `PATCH /user/active-org` + второй `GET /projects` не нужны.
      //
      // Почему это лечится ЗДЕСЬ, а не в вызывающем коде. Дверей, которые сами
      // чинят активную организацию, уже две («Выйти из организации» в
      // `pages/OrgTeam.tsx` — сперва перезапрос списка, ставящий метку, потом
      // собственное переключение; и будущие такие же). Снимать метку руками в
      // каждой — то самое «правило с двумя концами, доведённое до одного»:
      // третья дверь молча заведёт двойную запись. Правило «пишем выбор ⇒
      // поправка доиграна» живёт в одном месте — в самой записи.
      //
      // Снимается на PENDING, а не только на fulfilled: в браузере пассивные
      // эффекты React выполняются позже цепочки промисов вызывающей функции,
      // поэтому `pending` (он уходит синхронно в момент dispatch) успевает
      // погасить метку ДО того, как реакция её увидит. Ждать ответа сервера
      // было бы поздно.
      .addCase(switchActiveOrgThunk.pending, (state) => {
        state.autoCorrectedOrgId = null;
      })
      .addCase(switchActiveOrgThunk.fulfilled, (state, action) => {
        state.activeOrgId = action.payload;
        state.autoCorrectedOrgId = null;
      });
  },
});

export const { setActiveOrg, clearAutoCorrectedOrg } =
  organizationsSlice.actions;
export default organizationsSlice.reducer;
